import { useEffect, useRef, useState } from "react";
import SVG from "react-inlinesvg";
import { MentionSuggestionsContainer } from "src/components/PersistentChat/mentions/MentionSuggestionsContainer";
import { WithReset } from "src/sudokit/Layout/BoxReset";
import { useDropdownNavigation } from "../../useDropdownNavigation";

export const ModelSelectDropdown = ({ options, currentValue, onSelect }) => {
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [modelSearchQuery, setModelSearchQuery] = useState("");
  const modelDropdownRef = useRef(null);
  const modelListRef = useRef(null);
  const modelSearchInputRef = useRef(null);
  const selectedModelOption = options.find((option) => option.value === currentValue)
    || { value: currentValue, label: currentValue, subLabel: "" };
  const shouldShowModelSearchInput = modelSearchQuery.length > 0;
  const filteredModelOptions = options.filter((option) => {
    const normalizedQuery = modelSearchQuery.trim().toLowerCase();
    if (!normalizedQuery) return true;
    return (
      option.label.toLowerCase().includes(normalizedQuery)
      || option.value.toLowerCase().includes(normalizedQuery)
      || String(option.subLabel || "").toLowerCase().includes(normalizedQuery)
    );
  });
  const {
    highlightedIndex: highlightedModelIndex,
    setHighlightedIndex: setHighlightedModelIndex,
    resetHighlightIndex,
    handleNavigationKeyDown,
  } = useDropdownNavigation({
    itemCount: filteredModelOptions.length,
    listRef: modelListRef,
    optionIndexAttribute: "data-model-option-index",
    isActive: isModelMenuOpen,
    stickyTopSelector: ".modelSelectSearch",
  });

  const selectModel = (modelValue) => {
    onSelect(modelValue);
    setModelSearchQuery("");
    setIsModelMenuOpen(false);
  };

  const openModelMenu = (initialQuery = "") => {
    setModelSearchQuery(initialQuery);
    if (initialQuery) {
      setHighlightedModelIndex(0);
    } else {
      const selectedIndex = Math.max(
        0,
        options.findIndex((option) => option.value === currentValue)
      );
      setHighlightedModelIndex(selectedIndex);
    }
    setIsModelMenuOpen(true);
  };

  useEffect(() => {
    if (!isModelMenuOpen) return;

    const handleClickOutside = (event) => {
      if (!(event.target instanceof Node)) return;
      if (!modelDropdownRef.current?.contains(event.target)) {
        setModelSearchQuery("");
        setIsModelMenuOpen(false);
      }
    };

    const handleDocumentKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setModelSearchQuery("");
        setIsModelMenuOpen(false);
        return;
      }

      const target = event.target;
      if (target instanceof HTMLElement) {
        const tagName = target.tagName.toLowerCase();
        if (tagName === "input" || tagName === "textarea" || target.isContentEditable) {
          return;
        }
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Backspace") {
        event.preventDefault();
        setModelSearchQuery((prev) => prev.slice(0, -1));
        resetHighlightIndex();
        return;
      }

      if (event.key.length === 1) {
        event.preventDefault();
        setModelSearchQuery((prev) => {
          if (event.key === " " && prev.length === 0) return prev;
          return `${prev}${event.key}`;
        });
        resetHighlightIndex();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [isModelMenuOpen, options, currentValue]);

  useEffect(() => {
    if (!isModelMenuOpen || !shouldShowModelSearchInput) return;
    requestAnimationFrame(() => {
      modelSearchInputRef.current?.focus();
      const length = modelSearchInputRef.current?.value?.length || 0;
      modelSearchInputRef.current?.setSelectionRange?.(length, length);
    });
  }, [isModelMenuOpen, shouldShowModelSearchInput]);

  const handleModelTriggerKeyDown = (event) => {
    if (!isModelMenuOpen && event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey) {
      event.preventDefault();
      openModelMenu(event.key);
      return;
    }

    if (event.key === "ArrowDown") {
      if (!isModelMenuOpen) {
        event.preventDefault();
        openModelMenu();
        return;
      }
    }

    if (event.key === "ArrowUp") {
      if (!isModelMenuOpen) {
        event.preventDefault();
        openModelMenu();
        return;
      }
    }

    if ((event.key === "Enter" || event.key === " ") && !isModelMenuOpen) {
      event.preventDefault();
      openModelMenu();
      return;
    }

    if (isModelMenuOpen) {
      handleNavigationKeyDown(event, {
        onSelectIndex: (index) => {
          const highlightedOption = filteredModelOptions[index];
          if (highlightedOption) {
            selectModel(highlightedOption.value);
          }
        },
        onEscape: () => {
          setModelSearchQuery("");
          setIsModelMenuOpen(false);
        },
      });
    }
  };

  const handleModelSearchKeyDown = (event) => {
    handleNavigationKeyDown(event, {
      onSelectIndex: (index) => {
        const highlightedOption = filteredModelOptions[index];
        if (highlightedOption) {
          selectModel(highlightedOption.value);
        }
      },
      onEscape: () => {
        setModelSearchQuery("");
        setIsModelMenuOpen(false);
      },
    });
  };

  return (
    <div className="modelSelect modelSelectDropdown" ref={modelDropdownRef}>
      <button
        type="button"
        className="modelSelectTrigger use-component-styles sudokit"
        aria-haspopup="listbox"
        aria-expanded={isModelMenuOpen}
        aria-label="Model"
        onClick={() => {
          if (isModelMenuOpen) {
            setIsModelMenuOpen(false);
          } else {
            openModelMenu();
          }
        }}
        onKeyDown={handleModelTriggerKeyDown}
      >
        <span className="modelSelectTriggerLabel">
          {selectedModelOption.label}
        </span>
        <span className="modelSelectTriggerCaret" aria-hidden="true">
          <SVG
            src="/assets/settings_dropdown_icon.svg"
            className="modelSelectTriggerCaretIcon"
          />
        </span>
      </button>
      {isModelMenuOpen && (
        <div className="modelSelectMenu">
          <MentionSuggestionsContainer>
            <WithReset
              ref={modelListRef}
              className="modelSelectMenuList"
              role="listbox"
              aria-label="Model options"
            >
              {shouldShowModelSearchInput && (
                <WithReset className="modelSelectSearch">
                  <input
                    ref={modelSearchInputRef}
                    type="text"
                    className="modelSelectSearchInput"
                    value={modelSearchQuery}
                    onChange={(event) => {
                      setModelSearchQuery(event.target.value);
                      resetHighlightIndex();
                    }}
                    onKeyDown={handleModelSearchKeyDown}
                    placeholder="Search models"
                    aria-label="Search models"
                  />
                </WithReset>
              )}
              {filteredModelOptions.length > 0 ? filteredModelOptions.map((option, index) => {
                const isHighlighted = index === highlightedModelIndex;
                return (
                  <WithReset
                    key={option.value}
                    as="button"
                    type="button"
                    role="option"
                    aria-selected={option.value === currentValue}
                    data-model-option-index={index}
                    className={`modelSelectOption use-component-styles sudokit ${isHighlighted ? "modelSelectOption--highlighted" : ""}`}
                    onMouseEnter={() => setHighlightedModelIndex(index)}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      selectModel(option.value);
                    }}
                  >
                    <WithReset className="modelSelectOptionText">
                      <p className="modelSelectOptionLabel">{option.label}</p>
                      {option.subLabel && (
                        <p className="modelSelectOptionDescription">{option.subLabel}</p>
                      )}
                    </WithReset>
                  </WithReset>
                );
              }) : (
                <WithReset className="modelSelectNoResults">
                  No models matching "{modelSearchQuery}" found.
                </WithReset>
              )}
            </WithReset>
          </MentionSuggestionsContainer>
        </div>
      )}
    </div>
  );
};
